import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { validators } from '../../../services/utils/validators';
import { useFormik } from 'formik';
import {
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Stack,
  Switch,
} from '@mui/material';
import Typography from '@mui/material/Typography';

function OperatorContactInformation({
  payload,
  setPayload,
  submit,
  setCanNext,
  setSubmit,
  createEdit,
}) {
  const { t } = useTranslation();
  const [addBookepingInfo, setAddBookepingInfo] = useState(false);
  const [addTechnicalInfo, setAddTechnicalInfo] = useState(false);

  const validationSchema = yup.object().shape({
    genericContactPersonName: yup.string().required(t('Namereq')),
    genericContactPersonPosition: yup.string().required(t('PositionReq')),
    genericContactPersonPhone: yup.string().required(t('PhoneNumReq')),
    genericContactPersonEmail: yup
      .string()
      .matches(validators.EMAIL, t('wrongEmail'))
      .email(t('wrongEmail'))
      .required(t('emailReq')),

    bookKeepingContactPersonName:
      addBookepingInfo || createEdit === 'edit'
        ? yup.string().required(t('Namereq'))
        : yup.string(),
    bookKeepingContactPersonPosition:
      addBookepingInfo || createEdit === 'edit'
        ? yup.string().required(t('PositionReq'))
        : yup.string(),
    bookKeepingContactPersonPhone:
      addBookepingInfo || createEdit === 'edit'
        ? yup.string().required(t('PhoneNumReq'))
        : yup.string(),
    bookKeepingContactPersonEmail:
      addBookepingInfo || createEdit === 'edit'
        ? yup
            .string()
            .matches(validators.EMAIL, t('wrongEmail'))
            .email(t('wrongEmail'))
            .required(t('emailReq'))
        : yup.string(),

    technicalIssuesContactPersonName:
      addTechnicalInfo || createEdit === 'edit'
        ? yup.string().required(t('Namereq'))
        : yup.string(),
    technicalIssuesContactPersonPosition:
      addTechnicalInfo || createEdit === 'edit'
        ? yup.string().required(t('PositionReq'))
        : yup.string(),
    technicalIssuesContactPersonPhone:
      addTechnicalInfo || createEdit === 'edit'
        ? yup.string().required(t('PhoneNumReq'))
        : yup.string(),
    technicalIssuesContactPersonEmail:
      addTechnicalInfo || createEdit === 'edit'
        ? yup
            .string()
            .matches(validators.EMAIL, t('wrongEmail'))
            .email(t('wrongEmail'))
            .required(t('emailReq'))
        : yup.string(),
  });

  useEffect(() => {
    if (submit > 0) document.getElementById('subContactInfo').click();
    setSubmit(0);
  }, [submit]);

  const formik = useFormik({
    initialValues: {
      genericContactPersonName:
        payload?.contactPersonDetails?.contactPersonName || '',
      genericContactPersonPosition:
        payload?.contactPersonDetails?.contactPersonPosition || '',
      genericContactPersonEmail:
        payload?.contactPersonDetails?.contactPersonEmail || '',
      genericContactPersonPhone:
        payload?.contactPersonDetails?.contactPersonPhone || '',

      bookKeepingContactPersonName:
        payload?.bookkeepingContactPersonDetails?.contactPersonName || '',
      bookKeepingContactPersonPosition:
        payload?.bookkeepingContactPersonDetails?.contactPersonPosition || '',
      bookKeepingContactPersonEmail:
        payload?.bookkeepingContactPersonDetails?.contactPersonEmail || '',
      bookKeepingContactPersonPhone:
        payload?.bookkeepingContactPersonDetails?.contactPersonPhone || '',

      technicalIssuesContactPersonName:
        payload?.technicianContactDetails?.contactPersonName || '',
      technicalIssuesContactPersonPosition:
        payload?.technicianContactDetails?.contactPersonPosition || '',
      technicalIssuesContactPersonEmail:
        payload?.technicianContactDetails?.contactPersonEmail || '',
      technicalIssuesContactPersonPhone:
        payload?.technicianContactDetails?.contactPersonPhone || '',
    },
    validationSchema: validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: (values) => {
      const finalValues = { ...values };
      if (addBookepingInfo === false && createEdit === 'create') {
        finalValues.bookKeepingContactPersonName =
          values.genericContactPersonName || '';
        finalValues.bookKeepingContactPersonPosition =
          values.genericContactPersonPosition || '';
        finalValues.bookKeepingContactPersonPhone =
          values.genericContactPersonPhone || '';
        finalValues.bookKeepingContactPersonEmail =
          values.genericContactPersonEmail || '';

        formik.setFieldValue(
          'bookKeepingContactPersonName',
          formik.values.genericContactPersonName || ''
        );
        formik.setFieldValue(
          'bookKeepingContactPersonPosition',
          formik.values.genericContactPersonPosition || ''
        );
        formik.setFieldValue(
          'bookKeepingContactPersonPhone',
          formik.values.genericContactPersonPhone || ''
        );
        formik.setFieldValue(
          'bookKeepingContactPersonEmail',
          formik.values.genericContactPersonEmail || ''
        );

        formik.setFieldTouched('bookKeepingContactPersonName', false, false);
        formik.setFieldTouched(
          'bookKeepingContactPersonPosition',
          false,
          false
        );
        formik.setFieldTouched('bookKeepingContactPersonPhone', false, false);
        formik.setFieldTouched('bookKeepingContactPersonEmail', false, false);
      }

      if (addTechnicalInfo === false && createEdit === 'create') {
        finalValues.technicalIssuesContactPersonName =
          values.genericContactPersonName || '';
        finalValues.technicalIssuesContactPersonPosition =
          values.genericContactPersonPosition || '';
        finalValues.technicalIssuesContactPersonPhone =
          values.genericContactPersonPhone || '';
        finalValues.technicalIssuesContactPersonEmail =
          values.genericContactPersonEmail || '';

        formik.setFieldValue(
          'technicalIssuesContactPersonName',
          formik.values.genericContactPersonName || ''
        );
        formik.setFieldValue(
          'technicalIssuesContactPersonPosition',
          formik.values.genericContactPersonPosition || ''
        );
        formik.setFieldValue(
          'technicalIssuesContactPersonPhone',
          formik.values.genericContactPersonPhone || ''
        );
        formik.setFieldValue(
          'technicalIssuesContactPersonEmail',
          formik.values.genericContactPersonEmail || ''
        );
      }

      if (
        createEdit === 'create' &&
        (addBookepingInfo === false || addTechnicalInfo === false)
      ) {
        formik
          .validateForm()
          .then((errors) => {
            const isValid = Object.keys(errors || {}).length === 0;
            setCanNext(Boolean(isValid));
          })
          .catch(() => setCanNext(false));
      }

      let payloadToSet = {
        cpoId: payload?.cpoId,
        contactPersonDetails: {
          contactPersonName: finalValues.genericContactPersonName,
          contactPersonPosition: finalValues.genericContactPersonPosition,
          contactPersonEmail: finalValues.genericContactPersonEmail,
          contactPersonPhone: finalValues.genericContactPersonPhone,
        },
        bookkeepingContactPersonDetails: {
          contactPersonName: finalValues.bookKeepingContactPersonName,
          contactPersonPosition: finalValues.bookKeepingContactPersonPosition,
          contactPersonEmail: finalValues.bookKeepingContactPersonEmail,
          contactPersonPhone: finalValues.bookKeepingContactPersonPhone,
        },
        technicianContactDetails: {
          contactPersonName: finalValues.technicalIssuesContactPersonName,
          contactPersonPosition:
            finalValues.technicalIssuesContactPersonPosition,
          contactPersonEmail: finalValues.technicalIssuesContactPersonEmail,
          contactPersonPhone: finalValues.technicalIssuesContactPersonPhone,
        },
      };
      setPayload(payloadToSet);
    },
  });

  useEffect(() => {
    const hasTouched = Object.keys(formik?.touched || {}).length > 0;
    setCanNext(Boolean(formik?.isValid && hasTouched));
  }, [formik?.values, formik?.touched, formik?.isValid]);

  return (
    <>
      <form onSubmit={formik?.handleSubmit} noValidate>
        <Typography
          fontWeight={700}
          variant="subtitle1"
          id="autorisierung-uber-label"
        >
          {t('LBLOperatorGenericInfoSection')}
        </Typography>
        <Grid container spacing={6}>
          <Grid item md={6}>
            <TextField
              margin="dense"
              id="genericContactPersonName"
              label={t('Name')}
              variant="outlined"
              name={'genericContactPersonName'}
              required
              value={formik.values.genericContactPersonName}
              onChange={formik.handleChange}
              error={
                formik.touched.genericContactPersonName &&
                Boolean(formik.errors.genericContactPersonName)
              }
              helperText={
                formik.touched.genericContactPersonName &&
                formik.errors.genericContactPersonName
              }
              fullWidth
              my={2}
            />
          </Grid>
          <Grid item md={6}>
            <TextField
              margin="dense"
              id="genericContactPersonPosition"
              label={t('job')}
              variant="outlined"
              required
              fullWidth
              name={'genericContactPersonPosition'}
              my={2}
              value={formik?.values?.genericContactPersonPosition}
              onChange={formik?.handleChange}
              error={
                formik?.touched?.genericContactPersonPosition &&
                Boolean(formik?.errors?.genericContactPersonPosition)
              }
              helperText={
                formik?.touched?.genericContactPersonPosition &&
                formik?.errors?.genericContactPersonPosition
              }
            />
          </Grid>
          <Grid item md={6}>
            <TextField
              margin="dense"
              id="genericContactPersonPhone"
              label={t('PhoneNum')}
              variant="outlined"
              fullWidth
              my={2}
              required
              name={'genericContactPersonPhone'}
              value={formik?.values?.genericContactPersonPhone}
              onChange={formik?.handleChange}
              error={
                formik?.touched?.genericContactPersonPhone &&
                Boolean(formik?.errors?.genericContactPersonPhone)
              }
              helperText={
                formik?.touched?.genericContactPersonPhone &&
                formik?.errors?.genericContactPersonPhone
              }
            />
          </Grid>
          <Grid item md={6}>
            <TextField
              margin="dense"
              id="genericContactPersonEmail"
              label="E-Mail"
              variant="outlined"
              fullWidth
              required
              my={2}
              name={'genericContactPersonEmail'}
              value={formik?.values?.genericContactPersonEmail}
              onChange={formik?.handleChange}
              error={
                formik?.touched?.genericContactPersonEmail &&
                Boolean(formik?.errors?.genericContactPersonEmail)
              }
              helperText={
                formik?.touched?.genericContactPersonEmail &&
                formik?.errors?.genericContactPersonEmail
              }
            />
          </Grid>
        </Grid>
        <br></br>

        {createEdit === 'create' && (
          <Stack my={3} direction="row" alignItems="center" spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={addBookepingInfo || false}
                  onChange={(e) => {
                    setAddBookepingInfo(e.target.checked);
                  }}
                  color="primary"
                />
              }
              label={t('LBLAddBookeepingInfo')}
            />
          </Stack>
        )}

        {(addBookepingInfo || createEdit === 'edit') && (
          <>
            <Typography
              fontWeight={700}
              variant="subtitle1"
              id="LBLOperatorBillingRelatedInfoSection"
            >
              {t('LBLOperatorBillingRelatedInfoSection')}
            </Typography>
            <Grid container spacing={6}>
              <Grid item md={6}>
                <TextField
                  margin="dense"
                  id="bookKeepingContactPersonName"
                  label={'Name'}
                  variant="outlined"
                  name={'bookKeepingContactPersonName'}
                  required
                  value={formik?.values?.bookKeepingContactPersonName}
                  onChange={formik?.handleChange}
                  error={
                    formik?.touched?.bookKeepingContactPersonName &&
                    Boolean(formik?.errors?.bookKeepingContactPersonName)
                  }
                  helperText={
                    formik?.touched?.bookKeepingContactPersonName &&
                    formik?.errors?.bookKeepingContactPersonName
                  }
                  fullWidth
                  my={2}
                />
              </Grid>
              <Grid item md={6}>
                <TextField
                  margin="dense"
                  id="bookKeepingContactPersonPosition"
                  label={t('job')}
                  variant="outlined"
                  required
                  fullWidth
                  name={'bookKeepingContactPersonPosition'}
                  my={2}
                  value={formik?.values?.bookKeepingContactPersonPosition}
                  onChange={formik?.handleChange}
                  error={
                    formik?.touched?.bookKeepingContactPersonPosition &&
                    Boolean(formik?.errors?.bookKeepingContactPersonPosition)
                  }
                  helperText={
                    formik?.touched?.bookKeepingContactPersonPosition &&
                    formik?.errors?.bookKeepingContactPersonPosition
                  }
                />
              </Grid>
              <Grid item md={6}>
                <TextField
                  margin="dense"
                  id="bookKeepingContactPersonPhone"
                  label={t('PhoneNum')}
                  variant="outlined"
                  fullWidth
                  my={2}
                  required
                  name={'bookKeepingContactPersonPhone'}
                  value={formik?.values?.bookKeepingContactPersonPhone}
                  onChange={formik?.handleChange}
                  error={
                    formik?.touched?.bookKeepingContactPersonPhone &&
                    Boolean(formik?.errors?.bookKeepingContactPersonPhone)
                  }
                  helperText={
                    formik?.touched?.bookKeepingContactPersonPhone &&
                    formik?.errors?.bookKeepingContactPersonPhone
                  }
                />
              </Grid>
              <Grid item md={6}>
                <TextField
                  margin="dense"
                  id="bookKeepingContactPersonEmail"
                  label="E-Mail"
                  variant="outlined"
                  fullWidth
                  required
                  my={2}
                  name={'bookKeepingContactPersonEmail'}
                  value={formik?.values?.bookKeepingContactPersonEmail}
                  onChange={formik?.handleChange}
                  error={
                    formik?.touched?.bookKeepingContactPersonEmail &&
                    Boolean(formik?.errors?.bookKeepingContactPersonEmail)
                  }
                  helperText={
                    formik?.touched?.bookKeepingContactPersonEmail &&
                    formik?.errors?.bookKeepingContactPersonEmail
                  }
                />
              </Grid>
            </Grid>
          </>
        )}

        {createEdit === 'create' && (
          <Stack my={3} direction="row" alignItems="center" spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={addTechnicalInfo || false}
                  onChange={(e) => {
                    setAddTechnicalInfo(e.target.checked);
                  }}
                  color="primary"
                />
              }
              label={t('LBLAddTechnicalInfo')}
            />
          </Stack>
        )}
        {createEdit === 'edit' && (
          <>
            <br></br>
          </>
        )}
        {(addTechnicalInfo || createEdit === 'edit') && (
          <>
            <Typography
              fontWeight={700}
              variant="subtitle1"
              id="LBLOperatorTechnicalInfoSection"
            >
              {t('LBLOperatorTechnicalInfoSection')}
            </Typography>
            <Grid container spacing={6}>
              <Grid item md={6}>
                <TextField
                  margin="dense"
                  id="technicalIssuesContactPersonName"
                  label={'Name'}
                  variant="outlined"
                  name={'technicalIssuesContactPersonName'}
                  required
                  value={formik?.values?.technicalIssuesContactPersonName}
                  onChange={formik?.handleChange}
                  error={
                    formik?.touched?.technicalIssuesContactPersonName &&
                    Boolean(formik?.errors?.technicalIssuesContactPersonName)
                  }
                  helperText={
                    formik?.touched?.technicalIssuesContactPersonName &&
                    formik?.errors?.technicalIssuesContactPersonName
                  }
                  fullWidth
                  my={2}
                />
              </Grid>
              <Grid item md={6}>
                <TextField
                  margin="dense"
                  id="technicalIssuesContactPersonPosition"
                  label={t('job')}
                  variant="outlined"
                  required
                  fullWidth
                  name={'technicalIssuesContactPersonPosition'}
                  my={2}
                  value={formik?.values?.technicalIssuesContactPersonPosition}
                  onChange={formik?.handleChange}
                  error={
                    formik?.touched?.technicalIssuesContactPersonPosition &&
                    Boolean(
                      formik?.errors?.technicalIssuesContactPersonPosition
                    )
                  }
                  helperText={
                    formik?.touched?.technicalIssuesContactPersonPosition &&
                    formik?.errors?.technicalIssuesContactPersonPosition
                  }
                />
              </Grid>
              <Grid item md={6}>
                <TextField
                  margin="dense"
                  id="technicalIssuesContactPersonPhone"
                  label={t('PhoneNum')}
                  variant="outlined"
                  fullWidth
                  my={2}
                  required
                  name={'technicalIssuesContactPersonPhone'}
                  value={formik?.values?.technicalIssuesContactPersonPhone}
                  onChange={formik?.handleChange}
                  error={
                    formik?.touched?.technicalIssuesContactPersonPhone &&
                    Boolean(formik?.errors?.technicalIssuesContactPersonPhone)
                  }
                  helperText={
                    formik?.touched?.technicalIssuesContactPersonPhone &&
                    formik?.errors?.technicalIssuesContactPersonPhone
                  }
                />
              </Grid>
              <Grid item md={6}>
                <TextField
                  margin="dense"
                  id="technicalIssuesContactPersonEmail"
                  label="E-Mail"
                  variant="outlined"
                  fullWidth
                  required
                  my={2}
                  name={'technicalIssuesContactPersonEmail'}
                  value={formik?.values?.technicalIssuesContactPersonEmail}
                  onChange={formik?.handleChange}
                  error={
                    formik?.touched?.technicalIssuesContactPersonEmail &&
                    Boolean(formik?.errors?.technicalIssuesContactPersonEmail)
                  }
                  helperText={
                    formik?.touched?.technicalIssuesContactPersonEmail &&
                    formik?.errors?.technicalIssuesContactPersonEmail
                  }
                />
              </Grid>
            </Grid>
          </>
        )}

        <Button
          sx={{ display: 'none' }}
          id="subContactInfo"
          type="submit"
        ></Button>
      </form>
    </>
  );
}
export default OperatorContactInformation;
